import React, { useState, useEffect, useRef } from 'react';
import { Layers, Check, Plus, Pencil } from 'lucide-react';
import { ONBOARDING_ZONES } from '../../../config/onboardingData';
import { useClickOutside } from './useClickOutside';

interface ProfileZonesSelectorProps {
  selectedCities: string[];
  selectedZones: Record<string, string[]>;
  isEditing: boolean;
  onToggleEdit: () => void;
  onCloseEdit?: () => void;
  onToggleZone: (city: string, zone: string) => void;
  onSelectAllZones: (city: string) => void;
  onClearZones: (city: string) => void;
}

export const ProfileZonesSelector: React.FC<ProfileZonesSelectorProps> = ({
  selectedCities,
  selectedZones,
  isEditing,
  onToggleEdit,
  onCloseEdit,
  onToggleZone,
  onSelectAllZones,
  onClearZones,
}) => {
  const [activeTabCity, setActiveTabCity] = useState<string>(selectedCities[0] || '');
  const containerRef = useRef<HTMLDivElement>(null);
  useClickOutside(containerRef, onCloseEdit || onToggleEdit, isEditing);

  useEffect(() => {
    if (!selectedCities.includes(activeTabCity) && selectedCities.length > 0) {
      setActiveTabCity(selectedCities[0]);
    }
  }, [selectedCities, activeTabCity]);

  if (selectedCities.length === 0) return null;

  const currentAvailableZones = ONBOARDING_ZONES[activeTabCity]?.zones || [];
  const activeCitySelectedZones = selectedZones[activeTabCity] || [];
  const isAllSelected = currentAvailableZones.length > 0 &&
    activeCitySelectedZones.length === currentAvailableZones.length;

  return (
    <div ref={containerRef} className="space-y-1.5">
      <label className="block text-[11px] font-black text-slate-500 uppercase tracking-wider">
        Localidades y Comunas {activeTabCity ? `(${activeTabCity})` : ''}
      </label>

      {isEditing ? (
        <div className="w-full p-3.5 bg-white border-2 border-sky-500 ring-2 ring-sky-100 rounded-xl shadow-xs space-y-3 transition-all">
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm font-bold text-sky-800">
              Personaliza las zonas de cobertura:
            </span>
            <button
              type="button"
              onClick={onToggleEdit}
              title="Confirmar selección"
              className="p-1.5 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-600 transition-colors cursor-pointer"
            >
              <Check className="w-4 h-4 stroke-[2.5]" />
            </button>
          </div>

          {selectedCities.length > 1 && (
            <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-none">
              {selectedCities.map((city) => (
                <button
                  key={city}
                  type="button"
                  onClick={() => setActiveTabCity(city)}
                  className={`px-3 py-1.5 rounded-lg text-xs sm:text-sm font-bold whitespace-nowrap transition-all cursor-pointer ${
                    activeTabCity === city ? 'bg-sky-600 text-white shadow-xs' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  {city} ({selectedZones[city]?.length || 0})
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-100">
            <span className="text-xs sm:text-sm text-slate-600 font-semibold">Zonas en {activeTabCity}:</span>
            <button
              type="button"
              onClick={() => isAllSelected ? onClearZones(activeTabCity) : onSelectAllZones(activeTabCity)}
              className="text-xs font-bold text-sky-600 hover:text-sky-800 bg-sky-50 px-2.5 py-1 rounded-md transition-colors cursor-pointer"
            >
              {isAllSelected ? 'Desmarcar todas' : 'Marcar todas'}
            </button>
          </div>

          <div className="flex flex-wrap gap-2 pt-1">
            {currentAvailableZones.map((zone) => {
              const isChecked = activeCitySelectedZones.includes(zone);
              return (
                <button
                  key={zone}
                  type="button"
                  onClick={() => onToggleZone(activeTabCity, zone)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs sm:text-sm font-bold transition-all cursor-pointer shadow-xs ${
                    isChecked
                      ? 'bg-sky-600 text-white shadow-sky-200'
                      : 'bg-slate-50 text-slate-700 border border-slate-200 hover:border-sky-300 hover:bg-sky-50/50'
                  }`}
                >
                  {isChecked ? <Check className="w-3.5 h-3.5 stroke-[2.5]" /> : <Plus className="w-3.5 h-3.5 text-slate-400" />}
                  <span>{zone}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : (
        <div
          onClick={onToggleEdit}
          title="Hacer clic para modificar localidades"
          className="relative w-full pl-3.5 pr-10 py-2.5 bg-slate-200/80 hover:bg-slate-200 border border-slate-300/80 rounded-xl cursor-pointer transition-colors flex items-center min-h-[44px]"
        >
          {activeCitySelectedZones.length === 0 ? (
            <span className="text-xs sm:text-sm text-slate-500 font-semibold">
              Todas las localidades de {activeTabCity} (clic para personalizar)
            </span>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5 py-0.5">
              {activeCitySelectedZones.map((zone) => (
                <span
                  key={zone}
                  className="inline-flex items-center gap-1 px-2.5 py-1 bg-white/90 border border-slate-300/70 rounded-lg text-xs sm:text-sm font-bold text-slate-800 shadow-2xs"
                >
                  <Layers className="w-3.5 h-3.5 text-sky-600 flex-shrink-0" />
                  <span>{zone}</span>
                </span>
              ))}
            </div>
          )}

          <div className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-600 pointer-events-none">
            <Pencil className="w-4 h-4" />
          </div>
        </div>
      )}
    </div>
  );
};
